import React, { useEffect, useState } from "react";
import {
  IonCard,
  IonCol,
  IonGrid,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonTextarea,
} from "@ionic/react";
import { ICart } from "../../models";
import { OrderPriceDetails } from "./OrderPriceDetails";
import { capitalizeFirstLetter } from "../../common/utils";
import { useCommon } from "../../common/useCommon";
import { useCart } from "./useCart";
import { FrameAssembledBihWarning } from "../../components/FrameAssembledBihWarning/FrameAssembledBihWarning";
import { BihPriceWarning } from "../../components/BihPriceWarning/BihPriceWarning";
import {
  BIH_MAX_ORDER_PRICE_KM,
  BIH_PRICE_LIMIT_MESSAGE,
} from "../../common/constants";
import { convertPrice } from "../../common/currencyUtils";

interface Props {
  cart: ICart;
  updateCart: (cart: ICart) => void;
}

export const CartOrder: React.FC<Props> = ({ cart, updateCart }) => {
  const { currency, setCurrency, conversionRate } = useCommon();
  const {
    frames,
    framesAssembled,
    changeFrameNumber,
    changeFrameAssembledNumber,
    priceTotal,
  } = useCart();

  const [showAssembledWarning, setShowAssembledWarning] =
    useState<boolean>(false);
  const [showPriceWarning, setShowPriceWarning] = useState<boolean>(false);

  const priceTotalKM =
    conversionRate > 0 ? convertPrice(priceTotal(), "KM", conversionRate) : 0;
  const formattedPriceKM = priceTotalKM.toFixed(2).replace(".", ",") + " KM";
  const isPriceOverLimit = priceTotalKM > BIH_MAX_ORDER_PRICE_KM;

  const country = currency === "KM" ? "BIH" : "SRB";
  const phonePrefix = country === "BIH" ? "+387" : "";

  useEffect(() => {
    if (!cart.phone || cart.phone === "" || cart.phone === "+387") {
      updateCart({ ...cart, phone: phonePrefix, country });
    } else {
      updateCart({ ...cart, country });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phonePrefix]);

  const onCountryChange = (value: string) => {
    if (value === "BIH") {
      if (framesAssembled > 0) {
        setShowAssembledWarning(true);
      } else if (isPriceOverLimit) {
        setCurrency("KM");
        setShowPriceWarning(true);
      } else {
        setCurrency("KM");
      }
    } else {
      setCurrency("DIN");
    }
  };

  return (
    <>
      <IonGrid style={{ margin: 0, padding: 0 }}>
        <IonRow>
          <IonCol
            size="12"
            sizeMd="6"
            offsetMd="3"
            style={{ margin: 0, padding: 0 }}
          >
            <OrderPriceDetails deliveryInfo />

            <IonCard>
              <IonList>
                <IonItem>
                  <IonLabel color="medium" position="stacked">
                    Država
                  </IonLabel>
                  <IonSelect
                    value={country}
                    onIonChange={(e) => onCountryChange(e.detail.value)}
                    interface="popover"
                  >
                    <IonSelectOption value="SRB">Srbija</IonSelectOption>
                    <IonSelectOption value="BIH" disabled={conversionRate <= 0}>
                      Bosna i Hercegovina
                    </IonSelectOption>
                  </IonSelect>
                </IonItem>
                <IonItem>
                  <IonLabel
                    color="medium"
                    position="stacked"
                    className={
                      !!cart.name && cart.name.length > 3 ? "" : "required"
                    }
                  >
                    Ime i prezime
                  </IonLabel>
                  <IonInput
                    type="text"
                    // name="name"
                    // autocomplete="name"
                    required
                    value={cart.name}
                    onIonInput={(e: any) =>
                      updateCart({
                        ...cart,
                        name: capitalizeFirstLetter(e.detail.value),
                      })
                    }
                  />
                </IonItem>
                <IonItem aria-required={true}>
                  <IonLabel
                    color="medium"
                    position="stacked"
                    className={
                      !!cart.address && cart.address.length > 5
                        ? ""
                        : "required"
                    }
                  >
                    Adresa
                  </IonLabel>
                  <IonInput
                    type="text"
                    // name="street-address"
                    // autocomplete="street-address"
                    required
                    value={cart.address}
                    onIonInput={(e: any) =>
                      updateCart({
                        ...cart,
                        address: capitalizeFirstLetter(e.detail.value),
                      })
                    }
                  />
                </IonItem>
                <IonItem>
                  <IonLabel
                    color="medium"
                    position="stacked"
                    className={
                      !!cart.zip && cart.zip.length > 3 ? "" : "required"
                    }
                  >
                    Poštanski broj
                  </IonLabel>
                  <IonInput
                    type="text"
                    // name="postal-code"
                    // autocomplete="postal-code"
                    required
                    value={cart.zip}
                    onIonInput={(e: any) =>
                      updateCart({ ...cart, zip: e.detail.value })
                    }
                  />
                </IonItem>
                <IonItem>
                  <IonLabel
                    color="medium"
                    position="stacked"
                    className={
                      !!cart.place && cart.place.length > 1 ? "" : "required"
                    }
                  >
                    Mesto
                  </IonLabel>
                  <IonInput
                    type="text"
                    // name="city"
                    // autocomplete="address-level2"
                    required
                    value={cart.place}
                    onIonInput={(e: any) =>
                      updateCart({
                        ...cart,
                        place: capitalizeFirstLetter(e.detail.value),
                      })
                    }
                  />
                </IonItem>
                <IonItem>
                  <IonLabel
                    color="medium"
                    position="stacked"
                    className={
                      !!cart.phone && cart.phone.length > 6 ? "" : "required"
                    }
                  >
                    Kontakt telefon
                  </IonLabel>
                  <IonInput
                    type="text"
                    // name="tel"
                    // autocomplete="tel"
                    placeholder={country === "BIH" ? "+387" : ""}
                    required={!!cart.phone && cart.phone.length <= 6}
                    value={cart.phone}
                    onIonInput={(e: any) => {
                      let val: string = e.detail.value ?? "";
                      // Remove all chars that are not digits, +, -, or /
                      val = val.replace(/[^0-9+\-/]/g, "");
                      // + only allowed as first character
                      val = val.replace(/(?!^)\+/g, "");
                      // - and / not allowed as first character
                      if (val.startsWith("-") || val.startsWith("/")) val = val.slice(1);
                      updateCart({ ...cart, phone: val });
                    }}
                    onIonBlur={() => {
                      let val: string = cart.phone ?? "";
                      // - and / not allowed as last character
                      while (val.endsWith("-") || val.endsWith("/")) val = val.slice(0, -1);
                      updateCart({ ...cart, phone: val });
                    }}
                  />
                </IonItem>
                <IonItem>
                  <IonLabel color="medium" position="stacked">
                    Email
                  </IonLabel>
                  <IonInput
                    type="text"
                    // name="email"
                    // autocomplete="email"
                    value={cart.email}
                    onIonInput={(e: any) =>
                      updateCart({ ...cart, email: e.detail.value })
                    }
                  />
                </IonItem>
                <IonItem>
                  <IonLabel color="medium" position="stacked">
                    Napomena
                  </IonLabel>
                  <IonTextarea
                    rows={3}
                    value={cart.note}
                    onIonInput={(e: any) =>
                      updateCart({ ...cart, note: e.detail.value })
                    }
                  />
                </IonItem>
              </IonList>
            </IonCard>
          </IonCol>
        </IonRow>
      </IonGrid>
      <FrameAssembledBihWarning
        isOpen={showAssembledWarning}
        framesAssembled={framesAssembled}
        priceWarningMessage={
          isPriceOverLimit
            ? BIH_PRICE_LIMIT_MESSAGE(formattedPriceKM)
            : undefined
        }
        onDismiss={() => setShowAssembledWarning(false)}
        onRemove={() => {
          changeFrameAssembledNumber(0);
          setCurrency("KM");
          setShowAssembledWarning(false);
        }}
        onConvert={() => {
          changeFrameNumber(frames + framesAssembled);
          changeFrameAssembledNumber(0);
          setCurrency("KM");
          setShowAssembledWarning(false);
        }}
      />
      <BihPriceWarning
        isOpen={showPriceWarning}
        formattedPriceKM={formattedPriceKM}
        priceTotalKM={priceTotalKM}
        onDismiss={() => setShowPriceWarning(false)}
      />
    </>
  );
};
